import { formatCompactNumber, formatPercentage } from '@/lib/utils/formatNumber';
import { convertDbToDisplay } from '@/utils/currency';
import type { DeliverableAggregate } from '@/types/performanceMetrics';
import type { TopPost } from '@/hooks/useTopPerformingPosts';
import type { FormattedInfluencer } from '@/hooks/useAllInfluencers';
import type { TopInfluencer } from '../types/analytics.types';

export interface CampaignRawAggregates {
  totalFollowers: number;
  totalReach: number;
  totalEngagement: number;
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  totalInfluencers: number;
  totalPosts: number;
  totalViews: number;
  committed: number;
  spent: number;
  remaining: number;
  cpv: number;
  cpe: number;
}

/**
 * Pure calculation for campaign aggregates from deliverable aggregates and budget data.
 */
export function calculateCampaignAggregates(
  deliverableAggregates: DeliverableAggregate[],
  budgetData?: {
    totalBudget: number;
    amountSpent: number;
    remainingBudget: number;
    budgetUtilization?: number;
  }
): CampaignRawAggregates {
  const totalFollowers = deliverableAggregates.reduce(
    (s, d) => s + (d.influencer_followers || 0),
    0
  );
  const totalReach = totalFollowers * 0.9;
  const totalLikes = deliverableAggregates.reduce((s, d) => s + d.total_likes, 0);
  const totalComments = deliverableAggregates.reduce((s, d) => s + d.total_comments, 0);
  const totalShares = deliverableAggregates.reduce((s, d) => s + d.total_shares, 0);
  const totalEngagement = deliverableAggregates.reduce(
    (s, d) => s + d.total_likes + d.total_comments + d.total_shares + d.total_saves,
    0
  );
  const totalPosts = deliverableAggregates.reduce((s, d) => s + d.post_count, 0);
  const totalViews = deliverableAggregates.reduce((s, d) => s + d.total_views, 0);
  const totalInfluencers = deliverableAggregates.length;
  const committed = deliverableAggregates.reduce((s, d) => s + (d.fee_committed || 0), 0);
  const spent = budgetData?.amountSpent ?? 0;
  const remaining = (budgetData?.totalBudget ?? 0) - spent;
  const cpv = totalViews > 0 ? committed / totalViews : 0;
  const cpe = totalEngagement > 0 ? committed / totalEngagement : 0;

  return {
    totalFollowers,
    totalReach,
    totalEngagement,
    totalLikes,
    totalComments,
    totalShares,
    totalInfluencers,
    totalPosts,
    totalViews,
    committed,
    spent,
    remaining,
    cpv,
    cpe,
  };
}

/**
 * Computes Top Performing Influencers sorted by lowest CPE.
 */
export function calculateTopInfluencers(
  deliverableAggregates: DeliverableAggregate[],
  allInfluencers: FormattedInfluencer[],
  showAll: boolean = false
): TopInfluencer[] {
  if (deliverableAggregates.length === 0) return [];

  const influencerAvatarMap = new Map<string, string | null>(
    allInfluencers.map((inf) => [inf.id, inf.avatar ?? null])
  );

  type AggregateWithCPE = {
    aggregate: (typeof deliverableAggregates)[number];
    cpe: number | null;
  };

  const withCPE: AggregateWithCPE[] = deliverableAggregates.map((d) => ({
    aggregate: d,
    cpe:
      d.fee_committed && d.total_engagement > 0
        ? d.fee_committed / d.total_engagement
        : null,
  }));

  withCPE.sort((a, b) => {
    if (a.cpe === null && b.cpe === null) return 0;
    if (a.cpe === null) return 1;
    if (b.cpe === null) return -1;
    return a.cpe - b.cpe;
  });

  const sliced = showAll ? withCPE : withCPE.slice(0, 5);

  return sliced.map(({ aggregate: d, cpe: computedCPE }) => {
    const cpvValue =
      d.fee_committed && d.total_views > 0 ? d.fee_committed / d.total_views : null;

    return {
      name: d.influencer_name,
      avatar: d.influencer_avatar ?? influencerAvatarMap.get(d.influencer_id) ?? null,
      followers: formatCompactNumber(d.influencer_followers || 0),
      views: formatCompactNumber(d.total_views),
      engagement: formatCompactNumber(d.total_engagement),
      cpe: computedCPE !== null ? `₹${computedCPE.toFixed(2)}` : '₹0.00',
      cpv: cpvValue !== null ? `₹${cpvValue.toFixed(2)}` : '₹0.00',
      roi: d.er_views !== null ? `${d.er_views.toFixed(1)}%` : '0%',
    };
  });
}

/**
 * Computes Top Performing Posts sorted by total engagement descending.
 */
export function calculateTopPosts(allPosts: TopPost[], showAll: boolean = false): TopPost[] {
  const sorted = [...allPosts].sort((a, b) => {
    const engagementA = (a.likes || 0) + (a.comments || 0) + (a.shares || 0);
    const engagementB = (b.likes || 0) + (b.comments || 0) + (b.shares || 0);
    return engagementB - engagementA;
  });

  return showAll ? sorted : sorted.slice(0, 5);
}
